const STORAGE_KEY = 'debug:button-logging';
const GLOBAL_NAMESPACE = '__VENUE_DEBUG__';
const GLOBAL_FLAG = '__VENUE_BUTTON_LOGGING__';
const DEFAULT_ENABLED = true;

type Listener = (value: boolean) => void;

let cachedValue: boolean | null = null;
const listeners = new Set<Listener>();

function parseBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on', 'enable', 'enabled'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off', 'disable', 'disabled'].includes(normalized)) return false;
  }
  return null;
}

function readFromLocalStorage(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return parseBoolean(stored);
  } catch {
    return null;
  }
}

function readFromSession(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return parseBoolean(stored);
  } catch {
    return null;
  }
}

function readFromQueryParams(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('buttonLogging') ?? params.get('debugButtons');
    return parseBoolean(value);
  } catch {
    return null;
  }
}

function readFromEnvironment(): boolean | null {
  const envValue = (import.meta as any)?.env?.VITE_DEBUG_BUTTON_LOGGING;
  return parseBoolean(envValue);
}

function readFromGlobalFlag(): boolean | null {
  if (typeof window === 'undefined') return null;
  const globalValue = (window as any)[GLOBAL_FLAG];
  return parseBoolean(globalValue);
}

function persistValue(value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Ignore storage failures (e.g., Safari private mode).
  }
}

function determineInitialValue(): boolean {
  const sources: Array<() => boolean | null> = [
    readFromGlobalFlag,
    readFromQueryParams,
    readFromSession,
    readFromLocalStorage,
    readFromEnvironment,
  ];
  for (const source of sources) {
    const result = source();
    if (result !== null) return result;
  }
  return DEFAULT_ENABLED;
}

function getValue(): boolean {
  if (cachedValue === null) {
    cachedValue = determineInitialValue();
  }
  return cachedValue;
}

function updateValue(next: boolean, { persist = true }: { persist?: boolean } = {}) {
  cachedValue = next;
  if (persist) persistValue(next);
  for (const listener of listeners) {
    try {
      listener(next);
    } catch {
      // Ignore listener errors so logging stays resilient.
    }
  }
}

export function isButtonLoggingEnabled(): boolean {
  return getValue();
}

export function setButtonLoggingEnabled(value: boolean, options?: { persist?: boolean }) {
  updateValue(value, options);
}

export function toggleButtonLogging(options?: { persist?: boolean }): boolean {
  const next = !getValue();
  updateValue(next, options);
  return next;
}

export function onButtonLoggingChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function initButtonLoggingControls() {
  if (typeof window === 'undefined') return;
  const namespace = ((window as any)[GLOBAL_NAMESPACE] = (window as any)[GLOBAL_NAMESPACE] || {});
  if (namespace.buttonLogging) return;
  namespace.buttonLogging = {
    enable: () => setButtonLoggingEnabled(true),
    disable: () => setButtonLoggingEnabled(false),
    toggle: () => toggleButtonLogging(),
    status: () => isButtonLoggingEnabled(),
  };
  if (typeof console !== 'undefined' && console.info) {
    try {
      console.info(
        '[button-logging] Controls available via window.__VENUE_DEBUG__.buttonLogging (enable | disable | toggle | status)',
      );
    } catch {
      // ignore
    }
  }
}

export function recordButtonLoggingPreference() {
  // Ensures the initial value is cached so controls reflect the right state.
  getValue();
}

// Provide an imperative handle that external scripts can use without the namespace helper.
if (typeof window !== 'undefined') {
  try {
    (window as any)[GLOBAL_FLAG] = getValue();
    onButtonLoggingChange((value) => {
      (window as any)[GLOBAL_FLAG] = value;
    });
  } catch {
    // Ignore failures when assigning to window in restricted environments.
  }
}
